import { observable, action, runInAction, makeObservable } from "mobx";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { isLikelyEmail } from "utils/isLikelyEmail";

class ResetPasswordStore {
  captchaKey: string = null;
  errorMessage: string = "";
  requestResetSuccess: boolean = false;

  constructor() {
    makeObservable(this, {
      captchaKey: observable,
      errorMessage: observable,
      requestResetSuccess: observable,
      reset: action.bound,
      requestResetPassword: action.bound,
      submitResetPassword: action.bound,
      setCaptchaKey: action.bound,
    });

    this.captchaKey = null;
    this.errorMessage = "";
    this.requestResetSuccess = false;
  }

  reset() {
    this.captchaKey = null;
    this.errorMessage = "";
    this.requestResetSuccess = false;
  }

  /**
   * Initialize reset password call with userID, email and captcha
   */
  async requestResetPassword(formData: API_RequestPasswordReset) {
    this.errorMessage = "";
    try {
      const body = { ...formData, recaptchaResponse: this.captchaKey };
      if (isLikelyEmail(formData.userId)) {
        body.unifiedAccount = true;
      }
      const response = await API.newData(API_URL.PASSWORD.requestPasswordReset(), body);
      runInAction(() => {
        if (response.booleanValue === true) {
          this.requestResetSuccess = true;
        }
      });
      return response;
    } catch (err) {
      runInAction(() => {
        this.errorMessage = "Error: Unknown!";
        if (err.response) {
          if (err.response.status === 409) {
            if (err.response.data && err.response.data.code === 1012) {
              this.errorMessage = "REDIRECT"; // string constant to redirect back to Login
            } else {
              this.errorMessage = "Invalid credentials";
            }
          }
        }
      });
      throw err;
    }
  }

  async submitResetPassword(formData: { key: string; password: string }) {
    this.errorMessage = "";
    try {
      const response = await API.newData(API_URL.PASSWORD.resetPassword(), formData);
      return response.booleanValue;
    } catch (err) {
      runInAction(() => {
        this.errorMessage = "Error: Unknown!";

        if (err.response) {
          if (err.response.status === 409) {
            if (err.response.data) {
              const errorCodes = {
                1011: "You are not allowed to change password.",
                1014: "Password does not match the security requirements.",
                1012: "REDIRECT",
                1015: "REDIRECT",
              };
              this.errorMessage = errorCodes[err.response.data.code] || "";
            }
          }
        }
      });
      throw err;
    }
  }

  setCaptchaKey(key?: string) {
    this.captchaKey = key;
  }
}

const resetPasswordStore = new ResetPasswordStore();

export { resetPasswordStore, ResetPasswordStore };
